/**
 * Rule context — raw React context and consumer hook.
 *
 * Keeps the `createContext` call and the `useRuleContext` hook together
 * in a `.ts` file (no JSX) so Vite's react-refresh plugin does not
 * complain about mixing component exports with utility exports.
 *
 * The provider component lives in the sibling `RuleContextProvider.tsx`
 * file — it imports `RuleContext` from here and owns the `<Provider>`
 * element. Splitting the two lets component hot-reload treat the
 * provider as a pure-component file.
 *
 * Default value is an empty object, **not** a thrown error, so
 * components wrapped by {@link withComponentRules} keep working on
 * pages that have not yet adopted a provider (they simply see an
 * empty context and any rule referencing a variable returns `false`
 * for that leaf).
 */

import { createContext, useContext } from 'react';

import type { RuleContextValue } from './types';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Module-level React context carrying the rule context bag.
 *
 * Exported so the sibling `RuleContextProvider` component can wrap
 * children in `<RuleContext.Provider>`. Consumers should read the
 * context via {@link useRuleContext} rather than `useContext(RuleContext)`
 * directly.
 */
export const RuleContext = createContext<RuleContextValue>({});

RuleContext.displayName = 'RuleContext';

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

/**
 * Read the current rule context bag from the closest
 * `RuleContextProvider` ancestor.
 *
 * Returns an empty object when called outside any provider — the hook
 * intentionally does **not** throw so that configurable components can
 * be dropped on pages that have not yet adopted a provider without
 * breaking the render tree.
 */
export function useRuleContext(): RuleContextValue {
  return useContext(RuleContext);
}
